/**
 * Copyright (c) Siemens 2016 - 2026
 * SPDX-License-Identifier: MIT
 */
import { A11yModule, CdkTrapFocus } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  signal,
  TemplateRef,
  untracked,
  viewChild
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { elementMenu, elementThumbnails } from '@siemens/element-icons';
import {
  HeaderWithDropdowns,
  SI_HEADER_WITH_DROPDOWNS,
  SiHeaderDropdownTriggerDirective
} from '@siemens/element-ng/header-dropdown';
import { addIcons, SiIconComponent } from '@siemens/element-ng/icon';
import { BOOTSTRAP_BREAKPOINTS, Breakpoints } from '@siemens/element-ng/resize-observer';
import { SiTranslatePipe, t } from '@siemens/element-translate-ng/translate';
import { of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/** Root component for the application header. */
@Component({
  selector: 'si-application-header',
  imports: [SiTranslatePipe, A11yModule, NgTemplateOutlet, SiIconComponent],
  templateUrl: './si-application-header.component.html',
  styleUrl: './si-application-header.component.scss',
  providers: [{ provide: SI_HEADER_WITH_DROPDOWNS, useExisting: SiApplicationHeaderComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiApplicationHeaderComponent implements HeaderWithDropdowns, OnDestroy {
  private static idCounter = 0;

  /**
   * Defines the minimum breakpoint which must be matched to expand the header / switch to desktop mode.
   *
   * @defaultValue 'sm'
   */
  readonly expandBreakpoint = input<'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'never'>('sm');
  /** Template reference for the launchpad content rendered inside the header. */
  readonly launchpad = input<TemplateRef<void>>();
  /**
   * Accessible label for the launchpad toggle button.
   *
   * @defaultValue
   * ```
   * t(() => $localize`:@@SI_APPLICATION_HEADER.LAUNCHPAD:Launchpad`)
   * ```
   */
  readonly launchpadLabel = input(t(() => $localize`:@@SI_APPLICATION_HEADER.LAUNCHPAD:Launchpad`));

  /** @internal */
  readonly closeMobileMenus = new Subject<void>();

  /** @internal */
  readonly mobileNavigationExpanded = signal(false);
  /** @internal */
  readonly hasNavigation = signal(false);

  protected readonly openDropdownCount = signal(0);
  protected readonly launchpadOpen = signal(false);
  protected readonly id = `__si-application-header-${SiApplicationHeaderComponent.idCounter++}`;
  protected toggleNavigation = t(
    () => $localize`:@@SI_APPLICATION_HEADER.TOGGLE_NAVIGATION:Toggle navigation`
  );
  protected injector = inject(Injector);
  protected readonly icons = addIcons({ elementThumbnails, elementMenu });

  private readonly navigationToggle = viewChild<ElementRef<HTMLDivElement>>('navigationToggle');
  private readonly focusTrap = viewChild.required(CdkTrapFocus);
  private breakpointObserver = inject(BreakpointObserver);
  private openDropdown?: SiHeaderDropdownTriggerDirective;
  private closeMobileSub = this.closeMobileMenus.subscribe(() => {
    this.closeMobileNavigation();
    this.closeLaunchpad();
  });

  /** @internal */
  readonly inlineDropdown = toSignal(
    toObservable(this.expandBreakpoint).pipe(
      switchMap(expandBreakpoint => {
        if (expandBreakpoint === 'never') {
          return of(true);
        }
        return this.breakpointObserver
          .observe(
            `(min-width: ${
              BOOTSTRAP_BREAKPOINTS[(expandBreakpoint + 'Minimum') as keyof Breakpoints]
            }px)`
          )
          .pipe(map(({ matches }) => !matches));
      })
    ),
    { initialValue: false }
  );

  constructor() {
    effect(() => {
      this.inlineDropdown();
      untracked(() => this.closeMobileMenus.next());
    });
  }

  ngOnDestroy(): void {
    this.closeMobileSub.unsubscribe();
    this.closeMobileMenus.next();
    this.closeMobileMenus.complete();
  }

  /** @internal */
  onDropdownItemTriggered(): void {
    this.closeMobileMenus.next();
  }

  /** @internal */
  dropdownClosed(trigger?: SiHeaderDropdownTriggerDirective): void {
    this.openDropdownCount.update(v => v - 1);
    if (trigger === this.openDropdown) {
      this.openDropdown = undefined;
    }
  }

  /** @internal */
  dropdownOpened(trigger?: SiHeaderDropdownTriggerDirective): void {
    this.openDropdown ??= trigger;
    this.openDropdownCount.update(v => v + 1);
    this.closeLaunchpad();
  }

  /** @internal */
  closeLaunchpad(): void {
    if (this.launchpadOpen()) {
      this.launchpadOpen.set(false);
      this.dropdownClosed();

      if (this.hasNavigation()) {
        // This is also fine in expanded view when the mobile toggle is not visible.
        // Then the focus call will be ignored.
        this.navigationToggle()?.nativeElement.focus();
      }
    }
  }

  /** @internal */
  openLaunchpad(): void {
    if (!this.launchpadOpen()) {
      this.dropdownOpened();
      this.closeMobileMenus.next();
      this.launchpadOpen.set(true);
    }
  }

  protected toggleMobileNavigationExpanded(): void {
    if (this.mobileNavigationExpanded()) {
      this.closeMobileNavigation();
    } else {
      this.openMobileNavigation();
    }
  }

  protected toggleLaunchpad(): void {
    if (this.launchpadOpen()) {
      this.closeLaunchpad();
    } else {
      this.openLaunchpad();
    }
  }

  protected navigationEscapePressed(): void {
    this.closeMobileNavigation();
    this.navigationToggle()?.nativeElement.focus();
  }

  protected backdropClicked(): void {
    this.closeMobileMenus.next();
  }

  private openMobileNavigation(): void {
    if (!this.mobileNavigationExpanded()) {
      this.closeMobileMenus.next();
      this.mobileNavigationExpanded.set(true);
      this.dropdownOpened();
      this.focusTrap().focusTrap.focusFirstTabbableElementWhenReady();
    }
  }

  private closeMobileNavigation(): void {
    if (this.mobileNavigationExpanded()) {
      this.mobileNavigationExpanded.set(false);
      this.openDropdown?.close();
      this.dropdownClosed();
    }
  }
}
